import { useState } from 'react'
import { Formik } from 'formik'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import { Box } from '@dhl-ui/components'
import { useGetAdminActionsActionid, usePutAdminActionsActionid, type ActionUpdate } from '@dhl/receiver-dhl-extra-api'
import { BackButton } from '~/temp-ds-component/BackButton'

import { useNotificationMessage } from '~/common'

import { GenericCancelDialog } from '~/common/components'
import { links } from '~/AppRoutes'
import { PromotionForm, createValidationSchema } from '~/modules/dhl-extra/components'

type Params = {
  id: string
}

export const EditPromotionPage = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { id } = useParams<Params>()
  const { showError, showSuccess } = useNotificationMessage()
  const [cancelDialogOpen, setCancelDialogOpen] = useState<boolean>(false)
  const { data: promotion } = useGetAdminActionsActionid(id!, {
    query: {
      enabled: !!id,
    },
  })
  const { mutateAsync: updatePromotion } = usePutAdminActionsActionid()
  const validationSchema = createValidationSchema(t)

  const handleBack = (dirty: boolean) =>
    dirty && id ? setCancelDialogOpen(true) : navigate(links.promotionsOverviewLink())

  const initialValues: Partial<ActionUpdate> = {
    ...promotion,
    actionType: promotion?.actionType as any,
  }

  const handleSubmit = async (values: ActionUpdate) => {
    try {
      await updatePromotion({
        actionId: id!,
        data: values,
      })

      showSuccess(t('admin.dhlExtraPage.promotionsPage.promotionEditSuccess'))

      navigate(links.promotionsOverviewLink())
    } catch {
      showError(t('admin.dhlExtraPage.promotionsPage.promotionEditFailed'))
    }
  }

  if (!promotion) {
    return null
  }

  return (
    <>
      <Formik
        initialValues={initialValues}
        validateOnMount
        validateOnChange
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ dirty }) => (
          <Box p={2}>
            <Box display="flex" justifyContent="space-between" pb={2}>
              <BackButton onClick={() => handleBack(dirty)}>
                {t('admin.dhlExtraPage.promotionsPage.promotionForm.backButtonTitle')}
              </BackButton>
            </Box>
            <PromotionForm
              id={id}
              title={t('admin.dhlExtraPage.promotionsPage.promotionForm.editTitle')}
              onCancel={() => handleBack(dirty)}
            />
          </Box>
        )}
      </Formik>
      <GenericCancelDialog
        open={cancelDialogOpen}
        onAccept={() => links.promotionsOverviewLink()}
        onReject={() => setCancelDialogOpen(false)}
      />
    </>
  )
}
